/**
 * @fileoverview Own validated browser session identity for protected workspaces.
 *
 * Server validation supplies authentication, onboarding, and employee context.
 * Identity transitions clear document snapshots so cached data cannot cross
 * accounts. A remembered browser flag is not an authenticated server session.
 *
 * `AppAccessContext.js` owns workspace availability; `App.js` owns route admission;
 * `store/Documents/dataApi.js` owns identity-scoped document cache resets.
 * Resource and administrator permissions remain backend responsibilities. This
 * context connects browser identity to those consumers without granting their
 * business operations.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ReactReduxContext } from 'react-redux'
import { clearDocumentSession } from './store/Documents/dataApi'
import api from './api'

export const AuthContext = createContext()

const DEFAULT_AUTH_STATE = {
  isAuthenticated: false,
  requiresOnboarding: false,
  employeeId: null,
  isAdmin: false,
  onboardingCompletedAt: null,
  onboardingBlockedReason: null,
}



function normalizeAuthState(payload = {}) {
  return {
    isAuthenticated: payload.valid === true || payload.isAuthenticated === true,
    requiresOnboarding: payload.requiresOnboarding === true,
    employeeId: payload.employeeId ?? null,
    isAdmin: payload.isAdmin === true,
    onboardingCompletedAt: payload.onboardingCompletedAt ?? null,
    onboardingBlockedReason: payload.onboardingBlockedReason ?? null,
  }
}



export function AuthProvider({ children }) {
  const reduxContext = useContext(ReactReduxContext)
  const identity = useRef(null)
  const resetDocumentIdentity = useCallback(

  (next) => {
    if (identity.current !== next && reduxContext?.store) clearDocumentSession(reduxContext.store)
    identity.current = next
  }, [reduxContext])
  const [authState, setAuthState] = useState(DEFAULT_AUTH_STATE)
  const [isLoading, setIsLoading] = useState(true)

  const applyAuthState = useCallback(

  (payload = {}) => {
    const next = normalizeAuthState(payload)
    resetDocumentIdentity(next.isAuthenticated ? String(next.employeeId) : null)
    setAuthState(next)
  }, [resetDocumentIdentity])

  const clearAuthState = useCallback(

  () => {
    resetDocumentIdentity(null)
    setAuthState({ ...DEFAULT_AUTH_STATE })
  }, [resetDocumentIdentity])

  const setIsAuthenticated = useCallback(

  (value) => {
    if (!value) resetDocumentIdentity(null)
    setAuthState((previous) => (
      value
        ? { ...previous, isAuthenticated: true }
        : { ...DEFAULT_AUTH_STATE }
    ))
  }, [resetDocumentIdentity])

  const validateToken = useCallback(

  async () => {
    try {
      const response = await api.get('/validate')
      applyAuthState(response.data)
    } catch (error) {
      console.error(error)
      clearAuthState()
    } finally {
      setIsLoading(false)
    }
  }, [applyAuthState, clearAuthState])

  useEffect(() => {
    validateToken()
  }, [validateToken])

  const contextValue = useMemo(() => ({
    ...authState,
    setIsAuthenticated,
    applyAuthState,
    clearAuthState,
    validateToken,
  }), [authState, setIsAuthenticated, applyAuthState, clearAuthState, validateToken])

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
